/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import MaterialLibrary from "App/geometry/material/material_library";
import RenderDocument from "App/render_document_interface";
import {mat4} from "gl-matrix";
import GeometryDescription from "App/geometry/description";
import ModelDescription from "App/model/description";
import BoundingBox from "App/utils/bounding_box";
import {RenderElement} from "App/model/elements/element";
import ZeropointSettings from "App/model/zeropoint_settings";
import {ConfigurationData} from "App/model/configuration";
import {LightingSettingsData} from "App/model/lighting_settings";
import GroupTree from "App/model/group_tree";
import Messenger from "App/messenger";
import {logError} from "App/debug";
import RenderObject from "App/geometry/objects/object";
import ProgressDispatchHelper from "App/utils/progress_dispatch_helper";
import {UPDATETYPES} from "App/render/webgl/renderables/batches/batch";
import {inURLBinaryData} from "App/storage/utils";
import IModel from "js_interface/src/structural_elements/model_interface";
import IScene from "js_interface/src/scene_interface";

export enum EDescriptionDownloadState {
    WAITING = 0,
    DOWNLOADED = 1,
    FAILED = 2
}

export default
class Model {

    protected static UIDGenerator: number = 0;

    protected readonly document: RenderDocument;
    protected readonly sceneAPI: IScene;
    protected readonly sceneAPIModelRef: IModel;
    protected UID: number = Model.UIDGenerator++;
    protected name: string = '';

    protected readonly materialLibrary: MaterialLibrary;

    protected geometryDescriptionNetworkQuery: {
        xhr: XMLHttpRequest | null,
        url: string
    };

    protected geometryDescription: GeometryDescription | null;

    // compute base url
    protected readonly _baseUrl: string = "";

    protected modelDescriptionNetworkQuery: {
        xhr: XMLHttpRequest | null,
        url: string
    };

    protected modelDescription: ModelDescription | null;

    protected descriptionDownloadCallback: any;
    protected descriptionDownloadFailedCallback: any;

    protected _tempShapeIDToElementMap: any = {}; /// temp data structure to match the element and geometry objects

    protected _onModelLoadTransformation: mat4;
    protected _hiddenByDefault: boolean;

    protected _stopLoading: boolean = false; // this is used to not allow the model and geometry description callback to be called
    // from dispatched and finished xmlhttp requests meanwhile the model was removed from document

    protected manipulated: boolean = false; // we have to remember if the model is manipulated or not, because the information stored in
    // GLBatches can be lost in case of recreating the batches due to some changes or new data
    // added to the document

    protected _descriptionsDownloadState: EDescriptionDownloadState = EDescriptionDownloadState.WAITING;

    protected _gcOptimization: any = { bbox: BoundingBox };

    // tslint:disable-next-line:max-line-length
    constructor(aSceneAPI: IScene, aSapiModel: IModel, aDocument: RenderDocument, aGeometryDescriptionUrl: string, aModelDescriptionUrl: string, aTransformation: mat4, aHiddenByDefault: boolean) {
        this.sceneAPI = aSceneAPI;
        this.sceneAPIModelRef = aSapiModel;
        this.document = aDocument;

        this.materialLibrary = new MaterialLibrary(this.sceneAPI);

        this.geometryDescriptionNetworkQuery = {
            xhr: null,
            url: aGeometryDescriptionUrl
        };

        // compute base url
        if (this.geometryDescriptionNetworkQuery.url.lastIndexOf("data:binary;base64") === -1) {
            let i = this.geometryDescriptionNetworkQuery.url.lastIndexOf("/");
            if (i !== -1) {
                this._baseUrl = this.geometryDescriptionNetworkQuery.url.substr(0, i + 1);
            }
        }

        this.modelDescriptionNetworkQuery = {
            xhr: null,
            url: aModelDescriptionUrl
        };

        this.geometryDescription = null;
        this.modelDescription = null;

        this.descriptionDownloadCallback = null;
        this.descriptionDownloadFailedCallback = null;

        this._tempShapeIDToElementMap = {}; /// temp data structure to match the element and geometry objects

        this._onModelLoadTransformation = aTransformation;
        this._hiddenByDefault = aHiddenByDefault;

        this._stopLoading = false; // this is used to not allow the model and geometry description callback to be called
                                   // from dispatched and finished xmlhttp requests meanwhile the model was removed from document

        this.manipulated = false; // we have to remember if the model is manipulated or not, because the information stored in
                                  // GLBatches can be lost in case of recreating the batches due to some changes or new data
                                  // added to the document

        this.init();
    }

    public init() {
        this.geometryDescriptionNetworkQuery.xhr = new XMLHttpRequest();
        this.geometryDescription = new GeometryDescription(this.document, this);

        this.modelDescriptionNetworkQuery.xhr = new XMLHttpRequest();
        this.modelDescription = new ModelDescription(this.document, this);

        this._gcOptimization = {
            bbox: new BoundingBox()
        };
    }

    public getName(): string {
        return this.name;
    }

    public getDocument(): RenderDocument {
        return this.document;
    }

    public getMaterialLibrary(): MaterialLibrary {
        return this.materialLibrary;
    }

    public getGeometryDescription(): GeometryDescription {
        return this.geometryDescription as GeometryDescription;
    }

    public getModelDescription(): ModelDescription {
        return this.modelDescription as ModelDescription;
    }

    public getDescriptionDownloadState(): EDescriptionDownloadState {
        return this._descriptionsDownloadState;
    }

    public getElementsByType(aElementType: string): RenderElement[]  {
        if (!this.modelDescription) {
            return [];
        }

        return this.modelDescription.getElementsByType(aElementType);
    }

    /***
     * This function help to speedup the mapping between geometry and model descriptions
     * @param aShapeUID Searched shape id in the shapes/outlines field of model elements instances
     * @returns {*}
     */
    public addShapeIDToElementMapping(aShapeId: number, aRenderElement: RenderElement) {
        let item = this._tempShapeIDToElementMap[aShapeId];
        if (item === undefined) {
            this._tempShapeIDToElementMap[aShapeId] = [];
        }

        this._tempShapeIDToElementMap[aShapeId].push(aRenderElement);
    }

    public getElementsByShapeID(aShapeParsedID: number): RenderElement[] {
        let elements = this._tempShapeIDToElementMap[aShapeParsedID];
        if (elements === undefined) {
            return [];
        }

        return elements;
    }

    public _clearShapeIDToElementMapping() {
        this._tempShapeIDToElementMap = {};
    }

    public getGeometryObjectByUID(aGeometryObjectUID: number): RenderObject | null {
        if (!this.geometryDescription) {
            return null;
        }

        return this.geometryDescription.getGeometryObjectByUID(aGeometryObjectUID);
    }

    public getUID(): number {
        return this.UID;
    }

    public isInternal(): boolean {
        return this.name === "Internal model";
    }

    public getSceneAPI(): IScene {
        return this.sceneAPI;
    }

    public getSceneAPIModel(): IModel {
        return this.sceneAPIModelRef;
    }

    public getSceneAPIModelUID(): number {
        return this.sceneAPIModelRef ? this.sceneAPIModelRef.getUID() : -1;
    }

    public getStandpoints(): any[] {
        if (!this.modelDescription) {
            return [];
        }
        return this.modelDescription.standpoints.getStandpoints();
    }

    public getViews(): any[] {
        if (!this.modelDescription) {
            return [];
        }
        return this.modelDescription.views.getViews();
    }

    public getGeoLocationSettings(): any {
        if (!this.modelDescription) {
            return {};
        }
        return this.modelDescription.geoLocationSettings.getProperties();
    }

    public getGeoLocationInfo(): any {
        if (!this.modelDescription) {
            return {};
        }
        return this.modelDescription.geoLocationSettings.getGeoLocationInfo();
    }

    public getZeropointSettingsRef(): ZeropointSettings | null {
        if (!this.modelDescription) {
            return null;
        }
        return this.modelDescription.zeropointSettings;
    }

    public getModelConfiguration(): ConfigurationData | null  {
        if (!this.modelDescription) {
            return null;
        }
        return this.modelDescription.configuration.getProperties();
    }

    public getLightingSettings(): LightingSettingsData | null {
        if (!this.modelDescription) {
            return null;
        }
        return this.modelDescription.lightingSettings.getProperties();
    }

    public setModelConfiguration(aConfiguration: ConfigurationData) {
        if (this.modelDescription) {
            this.modelDescription.configuration.setProperties(aConfiguration);
        }
    }

    public getOnModelLoadTransformationRef(): mat4 {
        return this._onModelLoadTransformation;
    }

    public getGroupTree(): GroupTree | null {
        if (!this.modelDescription) {
            return null;
        }
        return this.modelDescription.groupTree;
    }

    /**
     * Returns an unique group id.
     * Used for groups generated during a run-time.
     *
     * @return {int} Group id
     */
    public getUniqueGroupId(): number {
        let gt = this.getGroupTree();
        if (gt) {
            return gt.getUniqueGroupId();
        }
        else {
            return -1;
        }
    }

    /**
     * Returns a group visibility.
     *
     * @param {int} aGroupID internal ID (unique for a model), not a UID generated in the runtime!
     * @return {int} Three state visibility
     */
    public getGroupVisibility(aGroupID: number): number {
        let gt = this.getGroupTree();
        if (!gt) {
            return -1;
        }

        let group = gt.findGroup(aGroupID);
        if (!group) {
            return -1;
        }

        return group.getVisibility();
    }

    /**
     * Set visibility of all objects in specified aGroupId
     *
     * @param {int} aGroupID ID of group which visibility will be set
     * @param {bool} aVisible Visibility boolean value
     */
    public setGroupVisibility(aGroupID: number, aVisible: boolean) {
        let gt = this.getGroupTree();
        if (!gt) {
            return;
        }

        let group = gt.findGroup(aGroupID);
        if (!group) {
            return;
        }

        // first we need to change the model description
        group.setVisibility(aVisible ? 2 : 0);
        if (group.data.parent) {
            group.data.parent.updateParentVisibility();
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public setVisibility(aValue: boolean) {
        let gt = this.getGroupTree();
        if (!gt) {
            return;
        }

        gt.getRoot().setVisibility(aValue ? 2 : 0);
    }

    public getAnnotationConstructionGroups(): any {
        if (!this.modelDescription) {
            return null;
        }
        return this.modelDescription.annotations.getAnnotationConstructionGroups();
    }

    public getAnnotation(aFilterObject: any): any  {
        if (!this.modelDescription) {
            return null;
        }
        return this.modelDescription.annotations.getAnnotations(aFilterObject);
    }

    public getAnnotationsElements(aFilterObject: string): any[] {
        if (!this.modelDescription) {
            return [];
        }
        return this.modelDescription.annotations.getAnnotationsElements(aFilterObject);
    }

    /**
     *  Ondrej Prda 11.4.2019 Currently not implemented function getElementsByAnnotations
     */
    public getElementsByAnnotations(aAnnotationsIdArray: number[]): any {
        if (!this.modelDescription) {
            return null;
        }
        return this.modelDescription.elements.getElementsByAnnotations(aAnnotationsIdArray);
    }

    /**
     *
     * @param {function} aDescriptionDownloadedCallback
     * @param {function} aDescriptionDownloadFailedCallback
     */
    public downloadDescriptions(aDescriptionDownloadedCallback: any, aDescriptionDownloadFailedCallback: any) {
        this.descriptionDownloadCallback = aDescriptionDownloadedCallback;
        this.descriptionDownloadFailedCallback = aDescriptionDownloadFailedCallback;

        Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_startedToDownloadModelDescription", {
            modelId: this.getSceneAPIModelUID()
        });

        this.downloadModelDescription();
    }

    public downloadModelDescription() {
        if (!this.modelDescriptionNetworkQuery.xhr) {
            return;
        }
        let xhr: XMLHttpRequest = this.modelDescriptionNetworkQuery.xhr as XMLHttpRequest;

        if (xhr.overrideMimeType) {
            // xhr.overrideMimeType('text/xml');   //application/xhtml+xml   //@note mondzi: 'not well-formed' error in firefox
            xhr.overrideMimeType('application/json');
        }

        xhr.onreadystatechange = () => {
            if (xhr.readyState !== XMLHttpRequest.DONE) {
                return xhr;
            }

            if ((xhr.status !== 200) && (xhr.status !== 0)) {

                if (this._stopLoading) {
                    return xhr;
                }

                Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_modelDescriptionFailed", {
                    modelId: this.getSceneAPIModelUID(),
                    reason: 'FAILED: XHR status: ' + xhr.status
                });

                this._descriptionsDownloadState = EDescriptionDownloadState.FAILED;
                this.descriptionDownloadFailedCallback(this.UID, xhr.status);

                return xhr;
            }

            if (xhr.status === 200) {

                if (this._stopLoading) {
                    return;
                }

                if (this.modelDescription && this.modelDescription.parse(xhr.responseText)) {

                    // check if the hidden by default was set, then we pass through the model and reset the visibility of all groups to false
                    if (this._hiddenByDefault) {
                        let groupTree = this.getGroupTree();
                        if (groupTree) {
                            this.setGroupVisibility(groupTree.getRoot().getID(), false);
                        }
                    }

                    this.downloadGeometryDescription();
                }
                else {
                    let reasonStr = 'FAILED: Cannot parse the received model description!';
                    Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_modelDescriptionFailed", {
                        modelId: this.getSceneAPIModelUID(),
                        reason: reasonStr
                    });

                    this._descriptionsDownloadState = EDescriptionDownloadState.FAILED;
                    this.descriptionDownloadFailedCallback(this.UID, reasonStr);
                }
            }
        };

        let localData = null;
        let element = document.getElementById(this.modelDescriptionNetworkQuery.url);
        if (element) {
            localData = element.innerHTML;
        }

        if (localData) {
            // remember the callback
            let readystatechange = xhr.onreadystatechange;

            // fake the xmlhttp request finish
            xhr = {
                readyState: XMLHttpRequest.DONE,
                status: 200,
                responseText: localData
            } as any;

            readystatechange.call(this);
        }
        else if (this.modelDescriptionNetworkQuery.url.indexOf(inURLBinaryData()) !== -1) {

            let str = this.modelDescriptionNetworkQuery.url;
            let inlinedDataIdentifier: string = inURLBinaryData();
            let base64pos = str.indexOf(inlinedDataIdentifier);
            let response = str.substring(base64pos + inlinedDataIdentifier.length);

            // remember the callback
            let readystatechange = xhr.onreadystatechange;

            // fake the xmlhttp request finish
            xhr = {
                readyState: XMLHttpRequest.DONE,
                status: 200,
                responseText: window.atob(response)
            } as any;

            readystatechange.call(this);
        }
        else {
            xhr.open('GET', encodeURI(this.modelDescriptionNetworkQuery.url), true);

            // try {
            xhr.send(null);
            /*}
            catch (ex) {
                let reasonStr = 'FAILED: Exception when called xhr.send: ' + ex;
                Messenger.fireCDWKEvent(self.sceneAPI.getTargetTag(), "cwx3d_modelDescriptionFailed", {
                    modelId: self.getSceneAPIModelUID(),
                    reason: reasonStr
                });

                self._descriptionsDownloadState = EDescriptionDownloadState.FAILED;
                self.descriptionDownloadFailedCallback(self.UID, reasonStr);
            }*/
        }
    }

    public downloadGeometryDescription() {
        let xhr: XMLHttpRequest | any = this.geometryDescriptionNetworkQuery.xhr;
        if (xhr.overrideMimeType) {
            // xhr.overrideMimeType('text/xml');   //application/xhtml+xml   //@note mondzi: 'not well-formed' error in firefox
            xhr.overrideMimeType('application/json');
        }

        xhr.onreadystatechange = () => {
            if (xhr.readyState !== XMLHttpRequest.DONE) {
                return xhr;
            }

            if ((xhr.status !== 200) && (xhr.status !== 0)) {

                if (this._stopLoading) {
                    return xhr;
                }

                let reasonStr = 'FAILED: XHR status: ' + xhr.status;
                Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_modelDescriptionFailed", {
                    modelId: this.getSceneAPIModelUID(),
                    reason: reasonStr
                });

                this._descriptionsDownloadState = EDescriptionDownloadState.FAILED;
                this.descriptionDownloadFailedCallback(this.UID, reasonStr);

                return xhr;
            }

            if (xhr.status === 200) {

                if (this._stopLoading) {
                    return;
                }

                Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_modelDescriptionReady", {
                    modelId: this.getSceneAPIModelUID()
                });

                this.parseGeometryFromText(xhr.responseText);
                let modelName = this.modelDescription.projectInfo.getProperties().modelName;
                this.name = modelName;
                this.getGroupTree().setModelName(this.name);

                this.sceneAPIModelRef.createEntities();

                // set onModelLoadTransformation flag
                if (this._onModelLoadTransformation) {
                    let geometryObjects = this.geometryDescription.getGeometryObjects();
                    for (let geometryObject of geometryObjects) {
                        geometryObject.setOnLoadTransformation(true);
                    }
                }

                this._descriptionsDownloadState = EDescriptionDownloadState.DOWNLOADED;
                this.descriptionDownloadCallback(this.UID);

                if (this.getSceneAPIModel().getIsMainModel()) {

                    let viewPoint = this.geometryDescription.getViewpoint();
                    if (viewPoint) {
                        // solve viewpoint is placed in geometry description so we copy it to model description json
                        let modelDescriptionJSONFake = {
                            view: {
                                views: [{
                                    name: "default",
                                    cameraPosition: viewPoint.position,
                                    cameraTarget: viewPoint.centerOfRotation,
                                    fieldOfView: viewPoint.fieldOfView
                                },
                                {
                                    name: "current", // current equals default here
                                    cameraPosition: viewPoint.position,
                                    cameraTarget: viewPoint.centerOfRotation,
                                    fieldOfView: viewPoint.fieldOfView
                                }]
                            }
                        };
                        this.sceneAPI.setModulesFromModelDescription(modelDescriptionJSONFake);
                    }

                    // TODO: there should be much better handling that model was added (geometry created and its settings parsed) to renderer
                    // for each model:
                    // - there should be a flag 'initialized' in SceneAPI model
                    // - has an unique id
                    // add method to SceneAPI.Scene to check all models are initialized
                    // - if yes, notify external observers in application
                    // terrain (add, remove) must be handled in the same general way as any other model
                    // internal model should be handled in a same way

                    // TODO TB: synchronization of application own settings
                    this.sceneAPI.mainModelReady();

                    Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_mainModelReady", {
                        model: this.getSceneAPIModel()
                    });
                }
            }
        };

        // try to find out the modelDescriptionUrl in DOM as inlined local data
        let localData = null;
        let element = document.getElementById(this.geometryDescriptionNetworkQuery.url);
        if (element) {
            localData = element.innerHTML;
        }

        let readystatechange;
        if (localData) {
            // remember the callback
            readystatechange = xhr.onreadystatechange;

            // fake the xmlhttp request finish
            xhr = {
                readyState: XMLHttpRequest.DONE,
                status: 200,
                responseText: localData
            };

            // we need this because for local models, we need first the model id, and AFTER we have the id, we expect the model description arrival.
            setTimeout(readystatechange, 100);
        }
        else if (this.geometryDescriptionNetworkQuery.url.indexOf(inURLBinaryData()) !== -1) {

            let str = this.geometryDescriptionNetworkQuery.url;
            let inlinedDataIdentifier = inURLBinaryData();
            let base64pos = str.indexOf(inlinedDataIdentifier);
            let response = str.substring(base64pos + inlinedDataIdentifier.length);

            // remember the callback
            readystatechange = xhr.onreadystatechange;

            // fake the xmlhttp request finish
            xhr = {
                readyState: XMLHttpRequest.DONE,
                status: 200,
                responseText: window.atob(response)
            };

            // we need this because for local models, we need first the model id, and AFTER we have the id, we expect the model description arrival.
            setTimeout(readystatechange, 100);
        }
        else {
            xhr.open('GET', encodeURI(this.geometryDescriptionNetworkQuery.url), true);
            // try {
            xhr.send(null);
            /*}
            catch (ex) {
                let reasonStr = 'FAILED: Exception when called xhr.open: ' + ex;
                Messenger.fireCDWKEvent(self.sceneAPI.getTargetTag(), "cwx3d_modelDescriptionFailed", {
                    modelId: self.getSceneAPIModelUID(),
                    reason: reasonStr
                });

                self._descriptionsDownloadState = EDescriptionDownloadState.FAILED;
                self.descriptionDownloadFailedCallback(self.UID, reasonStr);
            }*/
        }
    }

    public stopModelAndGeometryDescriptionDownload() {
        this._stopLoading = true;

        if (this.modelDescriptionNetworkQuery.xhr) {
            this.modelDescriptionNetworkQuery.xhr.abort();
        }

        if (this.geometryDescriptionNetworkQuery.xhr) {
            this.geometryDescriptionNetworkQuery.xhr.abort();
        }
    }

    public parseGeometryFromText(aResponseText: string): boolean {
        try {
            this.geometryDescription.parse(JSON.parse(aResponseText));
            this._clearShapeIDToElementMapping(); // not needed anymore as the relations between elements and shapes are created already
        } catch (ex) {
            Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_geometryDescriptionFailed", {
                modelId: this.getSceneAPIModelUID(),
                reason: 'FAILED: Exception when parsing the geometry description json response text: ' + ex
            });

            logError('FAILED: Exception when parsing the geometry description json response text: ' + ex);

            return false;
        }

        return true;
    }

    public getIsMainModel(): boolean {
        return this.sceneAPIModelRef.getIsMainModel();
    }

    public getGeometryObjects(): RenderObject[] {
        return this.geometryDescription.getGeometryObjects();
    }

    /**
     * Return the path to which the geometry and texture urls inside the model.json file are relative
     *
     * @returns {string} the path to the model.json file
     */
    public getBaseUrl(): string {
        return this._baseUrl;
    }

    public getProjectInfo(): any {
        return this.modelDescription.projectInfo.getProperties();
    }

    public getCameraAnimationUrl(aAnimationID: number): string {
        let animation = this.modelDescription.animations.getCameraAnimation(aAnimationID);
        if (!animation) {
            return "";
        }

        return animation.getUrl();
    }

    public getViewpoint(): any  {
        return this.geometryDescription.getViewpoint();
    }

    /***
     * This method crates a geometry representation of model object (e.g. part, mesh, etc.) which is defined by render object and geometry description json data
     * @param aGroupID
     * @param aRenderElementType
     * @param aRenderElementDescriptionJSON
     * @param aGeometryObjectDescriptionJSON
     */
    // tslint:disable-next-line:max-line-length
    public addElement(aGroupID: number, aRenderElementTypeName: string, aRenderElementDescriptionJSON: any, aGeometryObjectDescriptionJSON: any, aAddToRenderer: boolean): RenderElement {
        // first we create the model object instance from the description
        let renderElement = this.modelDescription.addElement(aGroupID, aRenderElementTypeName, aRenderElementDescriptionJSON);

        // the we create geometry objects assigned to the model object
        for (let geometryObjectDescription of aGeometryObjectDescriptionJSON) {
            this.geometryDescription.addObject(geometryObjectDescription, renderElement);
        }

        if (aAddToRenderer) {
            let geometryObjects = renderElement.getGeometryObjects();
            for (let geometryObject of geometryObjects) {
                this.document.getRenderer().addShape(geometryObject);
                this.document.extendBoundingBox(geometryObject); // just extend bbox, no need to invalidate
            }
        }

        return renderElement;
    }

    /***
     * Remove model element from this render model
     * @param aRenderElement
     * @returns {boolean}
     */
    public removeElement(aRenderElement: RenderElement): boolean {
        let renderer = this.document.getRenderer();
        // first we remove the geometry objects from the renderer
        let geometryObjects = aRenderElement.getGeometryObjects();
        for (let geometryObject of geometryObjects) {
            renderer.removeShape(geometryObject);
        }

        this.document.invalidateBoundingBox();

        // then we remove the geometry objects from the model
        for (let geometryObject of geometryObjects) {
            this.geometryDescription.removeObject(geometryObject);
        }

        // finally we remove the model objects from the model
        this.modelDescription.removeElement(aRenderElement);

        return true;
    }

    public getBoundingBox(): BoundingBox {
        this._gcOptimization.bbox.init();
        let geometryObjects = this.geometryDescription.getGeometryObjects();
        for (let geometryObject of geometryObjects) {
            if (geometryObject.affectBoundingBox()) {
                this._gcOptimization.bbox.extendByPoint(geometryObject.getBoundingBoxMin());
                this._gcOptimization.bbox.extendByPoint(geometryObject.getBoundingBoxMax());
            }
        }

        return this._gcOptimization.bbox;
    }

    public getBoundingBoxForNPC() {
        this._gcOptimization.bbox.init();
        let geometryObjects = this.geometryDescription.getGeometryObjects();
        for (let geometryObject of geometryObjects) {
            if (geometryObject.affectBoundingBox()) {
                this._gcOptimization.bbox.extendByPoint(geometryObject.getBoundingBoxMinForNPC());
                this._gcOptimization.bbox.extendByPoint(geometryObject.getBoundingBoxMaxForNPC());
            }

        }

        return this._gcOptimization.bbox;
    }

    public getBoundingBoxFromVisibleObjectsForNPC(): BoundingBox {
        this._gcOptimization.bbox.init();
        let geometryObjects = this.geometryDescription.getGeometryObjects();
        for (let geometryObject of geometryObjects) {
            if (geometryObject.getVisibility() && geometryObject.affectBoundingBox()) {
                this._gcOptimization.bbox.extendByPoint(geometryObject.getBoundingBoxMinForNPC());
                this._gcOptimization.bbox.extendByPoint(geometryObject.getBoundingBoxMaxForNPC());
            }
        }

        return this._gcOptimization.bbox;
    }

    public getBoundingBoxFromVisibleObjects(): BoundingBox {
        this._gcOptimization.bbox.init();
        let geometryObjects = this.geometryDescription.getGeometryObjects();
        for (let geometryObject of geometryObjects) {
            if (geometryObject.getVisibility() && geometryObject.affectBoundingBox()) {
                this._gcOptimization.bbox.extendByPoint(geometryObject.getBoundingBoxMin());
                this._gcOptimization.bbox.extendByPoint(geometryObject.getBoundingBoxMax());
            }
        }

        return this._gcOptimization.bbox;
    }

    // TODO bad api, a group should have a method for selecting its elements
    public selectElementsInGroup(aGroupID: number, aValue: boolean) {
        let group = this.getGroupTree().findGroup(aGroupID);
        if (!group) {
            return false;
        }

        let selected = aValue === undefined ? true : aValue;
        group.setSelected(selected);

        let renderElements = [];
        group.getAllElements(renderElements);
        this._selectRenderElements(renderElements, selected);
    }

    public select(aValue: boolean) {
        let selected = aValue === undefined ? true : aValue;

        // select all
        this.getGroupTree().setSelected(selected);
        let renderElements = this.getGroupTree().getAllElements();
        this._selectRenderElements(renderElements, selected);
    }

    public _selectRenderElements(aRenderElements: RenderElement[], aSelected: boolean) {
        for (let renderElement of aRenderElements) {
            renderElement.setSelected(aSelected);
        }
        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public unselect() {
        this.select(false); // unselect all
    }

    public isGroupSelected(aGroupID: number): boolean {
        let group = this.getGroupTree().findGroup(aGroupID);
        if (!group) {
            return false;
        }

        let selected = group.getSelected();
        return selected;
    }

    /***
     * This just visually transform the geometry (without properly working primitive point picking),
     * shadows, scene bounding box updates etc. The transformation is taken from activity module.
     * @param aTransformationMatrix
     */
    public manipulate(aEnabled: boolean) {
        this.manipulated = (aEnabled === undefined) ? true : aEnabled;
        let shapes = this.getGeometryObjects();
        for (let shape of shapes) {
            shape.setNeedUpdate([
                UPDATETYPES.MANIPULATION,
                UPDATETYPES.NONINDEXED_MANIPULATON
            ]);
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public isManipulated(): boolean {
        return this.manipulated;
    }

    /**
     * Perform the transformation on the meshes (baking)
     * @param aTransformationMatrix
     */
    public transform(aTransformationMatrix: mat4) {
        let geometryObjects = this.geometryDescription.getGeometryObjects();

        let progress = new ProgressDispatchHelper(this.sceneAPI, "Model transformation", geometryObjects.length, 1000);
        progress.start();

        for (let j = 0; j < geometryObjects.length; j++) {
            geometryObjects[j].transform(aTransformationMatrix);
            progress.update(j);
        }

        this.document.invalidateBoundingBox();

        progress.finish();
    }

    /***
     * This is not yet completely general method but saves all data necessary to store the Terrain Model and a bit more.
     * This shall be used only for local models. Maybe later when there will be possibility to save the model from web via some
     * toolbar icon, we can use it too.
     *
     * TODO: make it more general when needed.
     */
    public saveToDOM() {
        let modelDIVElement = document.createElement('div');
        modelDIVElement.setAttribute("id", this.name);

        let modelDescription = document.createElement('div');
        modelDescription.setAttribute("id", "modelDescriptionFor_" + this.name);
        modelDescription.setAttribute("content", this.modelDescription.getJSONRepresentation());

        let textureBatchesForModel = document.createElement('div');
        textureBatchesForModel.setAttribute("id", "textureBatchesFor_" + this.name);

        let shapesForModel = document.createElement('div');
        shapesForModel.setAttribute("id", "shapesFor_" + this.name);

        let geomObjects = this.getGeometryObjects();
        let writtenTextures = {};
        for (let geomObject of geomObjects) {
            // store geometry of each shape in DOM and rename src property of binary geometry to point tom this DOM element
            let domElement = geomObject.saveToDOM();
            if (domElement) {
                shapesForModel.appendChild(domElement);
            }

            // store texture data
            let textureDataHandler = geomObject.getTextureDataHandler();
            if (textureDataHandler) {
                if (writtenTextures[textureDataHandler.getUID()] === undefined) {
                    let diffuseData = textureDataHandler.getDiffuseImageData();
                    if (diffuseData) {
                        let batch = document.createElement("cwTextureBatches");

                        batch.setAttribute("id", diffuseData.relativeUrl);
                        batch.setAttribute("base64data", window.btoa(String.fromCharCode.apply(null, diffuseData.compressedData)));

                        textureBatchesForModel.appendChild(batch);
                    }

                    writtenTextures[textureDataHandler.getUID()] = 1;
                }
            }
        }

        // we need to create the json repr of geometry description here as we modify the src property of bin geometry to
        // point to in DOM base64data
        let geometryDescription = document.createElement('div');
        geometryDescription.setAttribute("id", "geometryDescriptionFor_" + this.name);
        geometryDescription.setAttribute("content", this.geometryDescription.getJSONRepresentation());

        modelDIVElement.appendChild(modelDescription);
        modelDIVElement.appendChild(geometryDescription);
        modelDIVElement.appendChild(shapesForModel);
        modelDIVElement.appendChild(textureBatchesForModel);

        let body = document.getElementsByTagName('body');
        body[0].appendChild(modelDIVElement);
    }
}
